import { forwardRef, useCallback, Ref, RefAttributes, type JSX } from "react";

import { RadioGroup, useTheme } from "@mui/material";
import { FieldValues, Path, PathValue, useController } from "react-hook-form";

import { BaseFormRadioGroupProps, FormRadioGroupProps, SubscribedFormRadioGroupProps } from "./types";

import { mergeSx } from "@/forms-design-system/helpers";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useValidators } from "@/hooks/useValidators";

function FormRadioGroupInner<T extends FieldValues>(
  { control, value, defaultValue, rules, name, ...rest }: FormRadioGroupProps<T>,
  ref: Ref<HTMLDivElement>
) {
  return control && name ? (
    <SubscribedFormRadioGroup {...rest} ref={ref} control={control} rules={rules} name={name} value={value} />
  ) : (
    <BaseFormRadioGroup {...rest} name={name} ref={ref} defaultValue={defaultValue} />
  );
}

FormRadioGroupInner.displayName = "FormRadioGroup";

export const FormRadioGroup = forwardRef(FormRadioGroupInner) as <T extends FieldValues>(
  props: FormRadioGroupProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

function SubscribedFormRadioGroupInner<T extends FieldValues>(
  { control, defaultValue, name, onBlur, onChange, rules, ...rest }: SubscribedFormRadioGroupProps<T>,
  ref: Ref<HTMLDivElement>
) {
  const validators = useValidators(rules);
  const internalDefaultValue = defaultValue as PathValue<T, Path<T>> | undefined;

  const { field } = useController<T>({
    name,
    control,
    defaultValue: internalDefaultValue,
    rules: validators,
  });

  const handleBlur = useCallback(() => {
    onBlur?.();
    field.onBlur?.();
  }, [field, onBlur]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      onChange?.(event, value);
      field.onChange?.(value);
    },
    [field, onChange]
  );

  return <BaseFormRadioGroup {...rest} onBlur={handleBlur} onChange={handleChange} ref={ref} name={name} />;
}

const SubscribedFormRadioGroup = forwardRef(SubscribedFormRadioGroupInner) as <T extends FieldValues>(
  props: SubscribedFormRadioGroupProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

const BaseFormRadioGroup = forwardRef<HTMLDivElement, BaseFormRadioGroupProps>(({ sx, children, ...rest }, ref) => {
  const theme = useTheme();

  if (!theme.palette.forms || !theme.palette.formsComponents?.radio) {
    return <InvalidThemeErrorMessage />;
  }

  const styles = mergeSx(
    {
      display: "flex",
      flexDirection: "row",
      gap: theme.spacing(1),
      borderRadius: theme.radius(1),
      outlineColor: "transparent",
      outlineOffset: 4,
      transition: "outline 100ms ease-out",
      ["& > *"]: {
        flex: { xs: "0 0 calc(50% - 4px)", lg: "1 1 auto" },
      },
      [":has(.Mui-focusVisible)"]: {
        outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
      },
    },
    sx
  );

  return (
    <RadioGroup {...rest} sx={styles} ref={ref}>
      {children}
    </RadioGroup>
  );
});

BaseFormRadioGroup.displayName = "BaseFormRadioGroup";
